// Member card widget: data-365="member-card"
// Shows the signed-in member's scannable check-in QR for THIS venue. Browse/auth follows the same model as
// the my-bookings widget: if not signed in, a button full-page-redirects to the first-party 365 login and
// returns here; once signed in we read GET /api/memberships/me (Bearer) and render the venue's card.
//
// ROTATING QR (anti-sharing): for each active membership the widget fetches
// GET /api/memberships/{id}/card-token — the current 60s window's HMAC token + the next 4 (~5 min) — and
// rotates through them locally at each window boundary (using SERVER time, so the phone's clock doesn't
// matter), re-fetching before the bundle runs out. A screenshot of the QR dies within minutes. The QR is
// rendered LOCALLY (vendored qrcode-generator) — no external QR image service sees the token. If the
// bundle fetch fails (offline / older API), it falls back to the permanent static token so the member is
// never stranded; staffed check-in surfaces always accept the static token.
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import { sitePage, hasMount } from '../pages';
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, footer, clear, renderLoading, signOutRow } from '../ui';
import qrcode from '../vendor/qrcode-generator';

interface MyMembership {
  id?: string | null;
  venueId?: string | null;
  venueName?: string | null;
  planName?: string | null;
  status?: string | null;          // enum name: Active / PastDue / Cancelled / Incomplete
  memberCardToken?: string | null;
}

interface CardTokenDto {
  windowSeconds: number;
  serverTimeUtc: string;
  secondsLeftInWindow: number;
  tokens: string[];
}

const QR_SIZE = 220;
const WINDOW_SECONDS = 60;

/** Render a QR locally and return a data URL ('' on failure). */
function qrDataUrl(text: string): string {
  try {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    const cell = Math.max(2, Math.floor(QR_SIZE / (n + 4)));
    return qr.createDataURL(cell, cell * 2);
  } catch {
    return '';
  }
}

// Rotation timers from a previous render (sign-out/re-render must not leak intervals).
let activeTimers: number[] = [];
function clearTimers(): void {
  for (const t of activeTimers) window.clearInterval(t);
  activeTimers = [];
}

export function mountMemberCard(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'member-card') auth.clearPending();
  void render(root, cfg, api, auth);
}

async function render(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  clearTimers();
  clear(root);
  root.append(h('h3', undefined, 'Member Card'));

  if (!auth.isLoggedIn) {
    root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Sign in to show your member check-in QR.'));
    const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'member-card' }));
    root.append(login, footer());
    return;
  }

  const host = h('div');
  root.append(host);
  renderLoading(host, 'Member Card');

  let all: MyMembership[];
  try {
    all = await api.get<MyMembership[]>('/api/memberships/me');
  } catch (e) {
    clear(host);
    if (e instanceof AuthRequiredError) {
      host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Please sign in again to view your member card.'));
      const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
      login.addEventListener('click', () => auth.redirectToLogin({ t: 'member-card' }));
      host.append(login, footer());
    } else {
      host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your member card. Please try again.'));
      const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
      retry.addEventListener('click', () => void render(root, cfg, api, auth));
      host.append(retry, footer());
    }
    return;
  }

  const venueId = (cfg.venueId || '').toLowerCase();
  const mine = (all || []).filter((m) => (m.venueId || '').toLowerCase() === venueId);
  const active = mine.filter((m) => m.status === 'Active');

  clear(host);

  if (active.length === 0) {
    const alert = mine.length > 0
      ? h('div', { class: 'e365-alert info' }, 'Your membership here isn’t active right now, so it can’t be scanned.')
      : hasMount('membership')
        ? h('div', { class: 'e365-alert info' }, 'You’re not a member here yet. Join a plan above to get your check-in card.')
        : h('div', { class: 'e365-alert info' }, 'You’re not a member here yet. ',
            h('a', { href: sitePage('membership') }, 'Join a plan'), ' to get your check-in card.');
    host.append(
      alert,
      signOutRow(auth.profile?.email, () => { auth.logout(); void render(root, cfg, api, auth); }),
      footer(),
    );
    return;
  }

  host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Show this QR at the front desk to check in.'));
  for (const m of active) host.append(renderCard(m, api));
  host.append(
    signOutRow(auth.profile?.email, () => { auth.logout(); void render(root, cfg, api, auth); }),
    footer(),
  );
}

function renderCard(m: MyMembership, api: Api): HTMLElement {
  const card = h('div', {
    style: 'border:1px solid var(--e365-border); border-radius:var(--e365-radius); padding:14px; margin-bottom:10px; background:var(--e365-surface); text-align:center;',
  });
  if (m.planName) card.append(h('div', { style: 'font-weight:700; font-size:1.05rem;' }, m.planName));
  if (m.venueName) card.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-bottom:8px;' }, m.venueName));

  const img = h('img', {
    width: String(QR_SIZE),
    height: String(QR_SIZE),
    alt: 'Member QR code',
    style: 'border:1px solid var(--e365-border); border-radius:8px; padding:6px; background:#fff; max-width:100%; height:auto; image-rendering:pixelated;',
  }) as HTMLImageElement;
  const caption = h('div', { class: 'e365-muted', style: 'font-size:.78rem; margin-top:6px;' }, 'Scan at the front desk to check in');
  card.append(img, caption);

  if (m.id) {
    void rotate(m, img, caption, api);
  } else if (m.memberCardToken) {
    img.src = qrDataUrl(m.memberCardToken);
  }
  return card;
}

/** Drive the rotating QR: fetch the token bundle, swap at window boundaries, top up near the end. */
async function rotate(m: MyMembership, img: HTMLImageElement, caption: HTMLElement, api: Api): Promise<void> {
  let tokens: string[] = [];
  let baseWindow = 0;          // unix/60 of tokens[0]
  let serverUnixAtFetch = 0;
  let clientMsAtFetch = 0;
  let shownIdx = -1;
  let fetching = false;

  const fetchBundle = async (): Promise<boolean> => {
    if (fetching) return false;
    fetching = true;
    try {
      const dto = await api.get<CardTokenDto>(`/api/memberships/${m.id}/card-token`);
      if (!dto || !dto.tokens || dto.tokens.length === 0) return false;
      tokens = dto.tokens;
      serverUnixAtFetch = Math.floor(new Date(dto.serverTimeUtc).getTime() / 1000);
      clientMsAtFetch = Date.now();
      baseWindow = Math.floor(serverUnixAtFetch / WINDOW_SECONDS);
      shownIdx = -1;
      return true;
    } catch {
      return false;
    } finally {
      fetching = false;
    }
  };

  const tick = async (): Promise<void> => {
    const serverUnix = serverUnixAtFetch + Math.floor((Date.now() - clientMsAtFetch) / 1000);
    let idx = Math.floor(serverUnix / WINDOW_SECONDS) - baseWindow;
    const secondsLeft = WINDOW_SECONDS - (serverUnix % WINDOW_SECONDS);

    if (idx >= tokens.length - 1) await fetchBundle(); // entering the last window → top up

    const nowUnix = serverUnixAtFetch + Math.floor((Date.now() - clientMsAtFetch) / 1000);
    idx = Math.floor(nowUnix / WINDOW_SECONDS) - baseWindow;

    let stale = false;
    if (idx >= tokens.length) {
      idx = tokens.length - 1; // offline: server grants ±1 window of grace on the last token
      stale = true;
    }
    if (idx !== shownIdx) {
      shownIdx = idx;
      img.src = qrDataUrl(tokens[idx]);
    }
    caption.textContent = stale
      ? 'Reconnect to refresh your card'
      : `Live code · refreshes in ${secondsLeft}s`;
  };

  if (await fetchBundle()) {
    await tick();
    activeTimers.push(window.setInterval(() => void tick(), 1000));
  } else if (m.memberCardToken) {
    // Fallback: permanent static token (older API / offline first load).
    img.src = qrDataUrl(m.memberCardToken);
  }
}
